'use client'

import { useActionState, useEffect, useRef, useState } from 'react'
import { LuStar } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { Checkbox } from '@/core/components/form/checkbox'
import { Textarea } from '@/core/components/form/textarea'
import { usePreservedSubmit } from '@/core/hooks/use-preserved-submit'
import { cn } from '@/core/utils/common'

import {
  submitQuestion,
  submitReview,
  type CommunityFormState,
} from '../actions/community'

const INITIAL_STATE: CommunityFormState = { status: 'idle' }

function ResultMessage({ state }: { state: CommunityFormState }) {
  if (!state.message) return null
  return (
    <p
      role={state.status === 'error' ? 'alert' : 'status'}
      className={cn(
        'type-meta',
        state.status === 'error' ? 'text-error' : 'text-success'
      )}
    >
      {state.message}
    </p>
  )
}

export function ReviewForm({ productId }: { productId: string }) {
  const [state, action, pending] = useActionState(submitReview, INITIAL_STATE)
  const onSubmit = usePreservedSubmit(action)
  const [rating, setRating] = useState(5)
  const errors = state.fieldErrors ?? {}

  return (
    <form
      action={action}
      onSubmit={onSubmit}
      className="flex flex-col gap-3 bg-surface-soft p-4 over10:p-6"
    >
      <input type="hidden" name="productId" value={productId} />
      <input type="hidden" name="rating" value={rating} />
      <fieldset>
        <legend className="type-ui mb-1.5 font-medium">별점</legend>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((value) => (
            <Button
              key={value}
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`${value}점`}
              aria-pressed={value === rating}
              onClick={() => setRating(value)}
              className="min-h-10 px-1.5"
            >
              <LuStar
                aria-hidden="true"
                className={cn(
                  'size-6',
                  value <= rating ? 'fill-brand text-brand' : 'text-line-strong'
                )}
              />
            </Button>
          ))}
        </div>
      </fieldset>
      <Textarea
        label="리뷰 내용"
        name="content"
        rows={4}
        required
        placeholder="장착감, 호환 차종과 연식 등을 알려주시면 다른 오너분들께 도움이 됩니다."
        error={errors.content}
      />
      <ResultMessage state={state} />
      <div>
        <Button type="submit" loading={pending}>
          리뷰 등록
        </Button>
      </div>
    </form>
  )
}

export function QuestionForm({ productId }: { productId: string }) {
  const [state, action, pending] = useActionState(submitQuestion, INITIAL_STATE)
  const onSubmit = usePreservedSubmit(action)
  const formRef = useRef<HTMLFormElement>(null)
  const errors = state.fieldErrors ?? {}

  useEffect(() => {
    if (state.status === 'success') formRef.current?.reset()
  }, [state])

  return (
    <form
      ref={formRef}
      action={action}
      onSubmit={onSubmit}
      className="flex flex-col gap-3 bg-surface-soft p-4 over10:p-6"
    >
      <input type="hidden" name="productId" value={productId} />
      <Textarea
        label="문의 내용"
        name="question"
        rows={3}
        required
        placeholder="차종과 연식을 함께 적어주시면 더 정확하게 답변드립니다."
        error={errors.question}
      />
      <Checkbox
        name="isSecret"
        label={<span className="type-meta">비밀글로 문의하기</span>}
      />
      <ResultMessage state={state} />
      <div>
        <Button type="submit" loading={pending}>
          문의 등록
        </Button>
      </div>
    </form>
  )
}
